import { useState } from 'react';
import { X, Trash2, Edit3, Sparkles, Activity, Pill, TrendingUp, AlertTriangle, User, Clock, ChevronRight, Phone, Brain, Heart, MessageCircle, Calendar, MapPin, Users, Lightbulb } from 'lucide-react';
import { RISK_LABELS, RISK_COLORS, DOCTORS } from '../lib/constants';
import { aiGenerateReport, aiAnalyzePatient } from '../lib/mock';
import AIProgress from './AIProgress';

export default function PatientDetail({ patient, sessions, onClose, onEdit, onDelete }) {
  const [report, setReport] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [tab, setTab] = useState('overview');

  const flagged = sessions.filter(s => ['watch', 'alert', 'crisis'].includes(s.risk));
  const maxRisk = sessions.reduce((m, s) => Math.max(m, s.risk_score || 0), 0);

  const genReport = async () => {
    setGenLoading(true);
    try {
      const resp = await aiGenerateReport(patient.id);
      setReport(resp.report || 'لم يتم إنتاج التقرير');
    } catch (e) {
      setReport('⚠️ خطأ في توليد التقرير: ' + e.message);
    } finally { setGenLoading(false); }
  };

  const runAnalysis = async () => {
    setAnalyzeLoading(true);
    try {
      const resp = await aiAnalyzePatient(patient.id);
      setAnalysis(resp.analysis || 'لم يتم إنتاج التحليل');
    } catch (e) {
      setAnalysis('⚠️ خطأ في التحليل: ' + e.message);
    } finally { setAnalyzeLoading(false); }
  };

  const tabs = [
    { id: 'overview', label: 'نظرة عامة', icon: User },
    { id: 'clinical', label: 'الملف الطبي', icon: Brain },
    { id: 'sessions', label: 'الجلسات', icon: MessageCircle },
    { id: 'report', label: 'تقرير ذكي', icon: Sparkles },
    { id: 'analyze', label: 'دليل الداعم', icon: Lightbulb }
  ];

  return (
    <div className="card-soft mb-5 animate-slide-up overflow-hidden" style={{ boxShadow: '0 20px 60px rgba(139,127,240,0.15)' }}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-[var(--border-soft)]">
        <div className="flex items-start gap-3 mb-3">
          {patient.photo ? (
            <img src={patient.photo} alt={patient.name} className="w-14 h-14 rounded-2xl object-cover flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-extrabold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #8b7ff0, #5eb2a3)' }}>
              {patient.name.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-lg font-extrabold truncate" style={{ color: 'var(--text)' }}>{patient.name}</div>
            <div className="text-[12px] truncate flex items-center gap-2 flex-wrap" style={{ color: 'var(--text-2)' }}>
              {patient.age && <span>{patient.age} سنة</span>}
              {patient.age && '•'}
              <span className="truncate">{patient.diagnosis || '—'}</span>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--border-soft)] flex items-center justify-center transition-colors flex-shrink-0" data-testid="patient-detail-close-btn">
            <X className="w-4 h-4" style={{ color: 'var(--text-2)' }} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onEdit(patient)} className="flex-1 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 transition-all active:scale-95 shadow-sm" style={{ background: 'linear-gradient(135deg, #8b7ff0, #6a5cd8)', color: 'white' }} data-testid="patient-detail-edit-btn">
            <Edit3 className="w-3.5 h-3.5" />
            <span className="text-[12.5px] font-extrabold">تعديل ملف المريضة</span>
          </button>
          <button onClick={() => onDelete(patient.id)} className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors hover:scale-105 flex-shrink-0" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }} data-testid="patient-detail-delete-btn">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 py-2 flex gap-1.5 overflow-x-auto border-b border-[var(--border-soft)]">
        {tabs.map(t => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[12px] font-bold whitespace-nowrap transition-all ${isActive ? 'shadow-sm' : ''}`}
              style={isActive ? { background: 'var(--primary-bg)', color: 'var(--primary-dark)', border: '1px solid var(--primary)' } : { background: 'transparent', color: 'var(--text-2)', border: '1px solid transparent' }}>
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="p-5">
        {/* Overview */}
        {tab === 'overview' && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2.5">
              <SmallStat label="إجمالي الجلسات" value={sessions.length} color="#8b7ff0" bg="var(--primary-bg)" icon={Activity} />
              <SmallStat label="أعلى خطورة" value={`${maxRisk}/10`} color={maxRisk >= 7 ? 'var(--danger)' : maxRisk >= 4 ? 'var(--warning)' : 'var(--success)'} bg={maxRisk >= 7 ? 'var(--danger-bg)' : maxRisk >= 4 ? 'var(--warning-bg)' : 'var(--success-bg)'} icon={AlertTriangle} />
              <SmallStat label="تستدعي انتباه" value={flagged.length} color="var(--warning)" bg="var(--warning-bg)" icon={TrendingUp} />
            </div>

            <InfoGrid items={[
              patient.age && { icon: User, label: 'العمر', value: `${patient.age} سنة` },
              patient.gender && { icon: User, label: 'الجنس', value: patient.gender },
              patient.occupation && { icon: Users, label: 'المهنة', value: patient.occupation },
              patient.phone && { icon: Phone, label: 'الجوال', value: patient.phone },
              patient.address && { icon: MapPin, label: 'العنوان', value: patient.address },
              patient.therapy_start_date && { icon: Calendar, label: 'بدء العلاج', value: patient.therapy_start_date }
            ].filter(Boolean)} />

            {patient.emergency_name && (
              <div className="rounded-2xl p-3" style={{ background: 'var(--danger-bg)', border: '1px solid rgba(229,133,133,0.3)' }}>
                <div className="text-[11px] font-extrabold mb-1 flex items-center gap-1.5" style={{ color: '#a35555' }}>
                  <Phone className="w-3 h-3" />
                  جهة اتصال الطوارئ
                </div>
                <div className="text-[13px] font-bold" style={{ color: 'var(--text)' }}>{patient.emergency_name} <span className="text-[11px] font-normal" style={{ color: 'var(--text-2)' }}>({patient.emergency_relation})</span></div>
                <div className="text-[12px]" style={{ color: 'var(--text-2)' }}>{patient.emergency_phone}</div>
              </div>
            )}

            {patient.medications?.length > 0 && (
              <div>
                <SectionTitle icon={Pill} label="الأدوية الحالية" />
                <div className="flex flex-wrap gap-1.5">
                  {patient.medications.map((m, i) => {
                    const med = typeof m === 'string' ? { name: m } : m;
                    return (
                      <span key={i} className="rounded-lg px-2.5 py-1 text-[11.5px] font-bold" style={{ background: 'var(--secondary-bg)', color: 'var(--secondary-dark)', border: '1px solid var(--secondary-light)' }}>
                        {med.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Clinical file */}
        {tab === 'clinical' && (
          <div className="space-y-3">
            {patient.previous_diagnoses && <InfoBlock icon={Brain} title="تشخيصات سابقة" content={patient.previous_diagnoses} />}
            {patient.hospitalizations && <InfoBlock icon={AlertTriangle} title="دخول مستشفى" content={patient.hospitalizations} />}
            {patient.sleep_pattern && <InfoBlock icon={Clock} title="نمط النوم" content={patient.sleep_pattern} />}
            {patient.energy_pattern && <InfoBlock icon={Activity} title="مستوى الطاقة" content={patient.energy_pattern} />}
            {patient.triggers && <InfoBlock icon={AlertTriangle} title="المحفزات" content={patient.triggers} color="var(--warning)" bg="var(--warning-bg)" />}
            {patient.coping_mechanisms && <InfoBlock icon={Heart} title="آليات التأقلم" content={patient.coping_mechanisms} color="var(--success)" bg="var(--success-bg)" />}
            {patient.support_network && <InfoBlock icon={Users} title="شبكة الدعم" content={patient.support_network} />}
            {patient.personality_notes && <InfoBlock icon={Heart} title="الشخصية" content={patient.personality_notes} color="var(--primary-dark)" bg="var(--primary-bg)" />}
            {patient.thoughts_patterns && <InfoBlock icon={Brain} title="أنماط التفكير" content={patient.thoughts_patterns} color="var(--secondary-dark)" bg="var(--secondary-bg)" />}
            {patient.notes && <InfoBlock icon={ChevronRight} title="ملاحظات عامة" content={patient.notes} />}
            {!patient.previous_diagnoses && !patient.sleep_pattern && !patient.notes && (
              <div className="text-center py-10 text-[12.5px]" style={{ color: 'var(--text-3)' }}>
                لم يتم توثيق تفاصيل طبية بعد — اضغطي على “تعديل” لإضافتها
              </div>
            )}
          </div>
        )}

        {/* Sessions */}
        {tab === 'sessions' && (
          <div className="space-y-2">
            {sessions.length === 0 ? (
              <div className="text-center py-8 text-[12.5px]" style={{ color: 'var(--text-3)' }}>لا توجد جلسات بعد</div>
            ) : sessions.slice(0, 25).map(s => {
              const rc = RISK_COLORS[s.risk || 'none'];
              const doc = DOCTORS[s.doctor];
              const isVoice = s.doctor === 'voice' || (s.therapy_type || '').startsWith('voice_');
              const d = new Date(s.created_at);
              const dt = d.toLocaleDateString('ar-SA') + ' ' + d.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
              return (
                <div key={s.id} className="rounded-2xl p-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-soft)' }}>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="rounded-lg px-2 py-0.5 text-[10.5px] font-extrabold" style={{ background: rc.bg, color: rc.text, border: `1px solid ${rc.border}` }}>{RISK_LABELS[s.risk || 'none']}</span>
                    {isVoice ? (
                      <span className="text-[10.5px] flex items-center gap-1 font-semibold rounded-lg px-2 py-0.5" style={{ background: 'var(--secondary-bg)', color: 'var(--secondary-dark)', border: '1px solid var(--secondary-light)' }}>
                        🎙️ تسجيل صوتي{s.therapy_type === 'voice_session' ? ' • جلسة' : ''}
                      </span>
                    ) : (
                      doc && <span className="text-[10.5px] flex items-center gap-1 font-semibold" style={{ color: doc.id === 'k' ? 'var(--primary-dark)' : 'var(--secondary-dark)' }}>{doc.emoji} {doc.short}</span>
                    )}
                    {s.mood && <span className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>• {s.mood}</span>}
                    <span className="mr-auto text-[9.5px] font-semibold" style={{ color: 'var(--text-3)' }}>{dt}</span>
                  </div>
                  <div className="text-[12.5px] leading-relaxed" style={{ color: 'var(--text)' }}>« {(s.user_text || '').substring(0, 120)}{(s.user_text || '').length > 120 ? '…' : ''} »</div>
                  {s.ai_note && (
                    <div className="mt-1.5 text-[11px] flex items-start gap-1 font-semibold" style={{ color: 'var(--primary-dark)' }}>
                      <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {s.ai_note.substring(0, 120)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* AI Report */}
        {tab === 'report' && (
          <div>
            <div className="rounded-2xl p-4 mb-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-soft)', minHeight: 100 }}>
              {genLoading ? (
                <AIProgress active={genLoading} accent="#8b7ff0" />
              ) : report ? (
                <div className="text-[13px] whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--text)' }}>{report}</div>
              ) : (
                <div className="text-[12.5px] text-center py-4" style={{ color: 'var(--text-3)' }}>اضغطي لتوليد تقرير سريري شامل مبني على جميع بيانات المريضة</div>
              )}
            </div>
            <button onClick={genReport} disabled={genLoading} className="w-full rounded-2xl py-3 text-white text-[13.5px] font-extrabold flex items-center justify-center gap-2 disabled:opacity-40 hover:opacity-95 hover:scale-[1.005] active:scale-95 transition-all shadow-lg" style={{ background: 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }}>
              <Sparkles className="w-4 h-4" />
              {genLoading ? 'جارٍ التحليل…' : (report ? 'إعادة توليد التقرير' : 'توليد تقرير سريري ذكي')}
            </button>
          </div>
        )}

        {/* Analyze for Supporter */}
        {tab === 'analyze' && (
          <div>
            <div className="rounded-2xl p-3 mb-3" style={{ background: 'var(--honey-bg)', border: '1px solid var(--honey)' }}>
              <div className="text-[11px] font-extrabold mb-1 flex items-center gap-1.5" style={{ color: '#8a5f1a' }}>
                <Lightbulb className="w-3.5 h-3.5" />
                دليل خاص للداعم
              </div>
              <p className="text-[11.5px] leading-relaxed" style={{ color: '#6b4e14' }}>
                يحلل الذكاء الاصطناعي ملف المريضة كاملاً وينتج لك دليلاً مخصصاً يوضح: علامات التحذير، ماذا تفعل عند كل حالة مزاجية، أفضل طريقة للحديث معها، ورسائل داعمة مصممة لشخصيتها.
              </p>
            </div>
            <div className="rounded-2xl p-4 mb-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-soft)', minHeight: 100 }}>
              {analyzeLoading ? (
                <AIProgress active={analyzeLoading} accent="#d99149" stages={[
                  { icon: Lightbulb, label: 'أقرأ ملف المريضة وشخصيتها…', minSec: 3 },
                  { icon: Activity, label: 'أستخرج علامات التحذير من الجلسات…', minSec: 8 },
                  { icon: Heart, label: 'أصمم رسائل داعمة مخصصة…', minSec: 14 },
                ]} />
              ) : analysis ? (
                <div className="text-[13px] whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--text)' }}>{analysis}</div>
              ) : (
                <div className="text-[12.5px] text-center py-4" style={{ color: 'var(--text-3)' }}>اضغطي لتوليد دليل الدعم المخصص</div>
              )}
            </div>
            <button onClick={runAnalysis} disabled={analyzeLoading} className="w-full rounded-2xl py-3 text-white text-[13.5px] font-extrabold flex items-center justify-center gap-2 disabled:opacity-40 hover:opacity-95 hover:scale-[1.005] active:scale-95 transition-all shadow-lg" style={{ background: 'linear-gradient(135deg, #f0b16c, #d99149)' }}>
              <Lightbulb className="w-4 h-4" />
              {analyzeLoading ? 'يتم التحليل…' : (analysis ? 'إعادة تحليل' : 'توليد دليل الدعم')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="w-3.5 h-3.5" style={{ color: 'var(--text-2)' }} />
      <h3 className="text-[11px] font-extrabold uppercase tracking-wider" style={{ color: 'var(--text-2)' }}>{label}</h3>
    </div>
  );
}

function SmallStat({ label, value, color, bg, icon: Icon }) {
  return (
    <div className="rounded-2xl p-3 text-center" style={{ background: bg, border: `1px solid ${color}33` }}>
      <Icon className="w-4 h-4 mx-auto mb-1" style={{ color }} />
      <div className="text-[9.5px] font-bold mb-0.5" style={{ color: 'var(--text-2)' }}>{label}</div>
      <div className="text-xl font-extrabold" style={{ color }}>{value}</div>
    </div>
  );
}

function InfoGrid({ items }) {
  if (items.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <div key={i} className="flex items-center gap-2 rounded-xl p-2.5" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-soft)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--primary-bg)' }}>
              <Icon className="w-3.5 h-3.5" style={{ color: 'var(--primary-dark)' }} />
            </div>
            <div className="min-w-0">
              <div className="text-[9.5px] font-semibold" style={{ color: 'var(--text-2)' }}>{it.label}</div>
              <div className="text-[12px] font-bold truncate" style={{ color: 'var(--text)' }}>{it.value}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InfoBlock({ icon: Icon, title, content, color = 'var(--text-2)', bg = 'var(--surface-2)' }) {
  return (
    <div className="rounded-2xl p-3" style={{ background: bg, border: '1px solid var(--border-soft)' }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[11px] font-extrabold uppercase tracking-wider" style={{ color }}>{title}</span>
      </div>
      <div className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--text)' }}>{content}</div>
    </div>
  );
}
